import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BubbleMenu, Editor } from '@tiptap/react';
import { isEmpty } from '../../helpers/common';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import { LineDiv } from './styles';
import { rootStore } from '../../mobx/rootStore';
import { CHIP_SIZE, CHIP_TYPE, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE, ICON_SIZE } from '../../global/constants';
import IconButton from '../button/iconButton';
import Chip from '../chipComponent';
import TiptapDropdown from './tiptapDropdown';

interface TiptapBubbleMenuProps {
  editor: Editor;
  isBubbleMenuMaxWidth: boolean;
  isImproveModalOpen: boolean;
  setImproveModel: () => void;
  isUrlOpen: boolean;
  setIsUrlOpen: (value: boolean) => void;
  urlValue: string;
  setUrlValue: (value: string) => void;
}

const TiptapBubbleMenu: React.FC<TiptapBubbleMenuProps> = ({
  editor,
  isBubbleMenuMaxWidth,
  isImproveModalOpen,
  setImproveModel,
  isUrlOpen,
  setIsUrlOpen,
  urlValue,
  setUrlValue
}) => {
  // ref variables
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const bubbleMenuRef = useRef<HTMLDivElement | null>(null);
  // state variables
  const [dropdownCoords, setDropdownCoords] = useState({ top: 0, left: 0 });
  const [isDropdownOpen, setDropdownOpen] = useState(false);

  // Toggle dropdown
  const toggleDropdown = useCallback(() => {
    setDropdownOpen(!isDropdownOpen);
  }, [isDropdownOpen]);

  // dropdown position calculation
  const adjustDropdownPosition = useCallback(() => {
    const buttonRect = buttonRef.current?.getBoundingClientRect();
    const dropdown = dropdownRef.current;
    if (buttonRect && dropdown) {
      const dropdownHeight = dropdown.offsetHeight;
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;

      let position: 'top' | 'bottom' = 'bottom';
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        position = 'top';
      }

      setDropdownCoords({
        top: position === 'bottom' ? buttonRect.bottom : buttonRect.top - dropdownHeight,
        left: buttonRect.left
      });
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isDropdownOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    const bubbleMenu = bubbleMenuRef.current;

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', adjustDropdownPosition);
      window.addEventListener('scroll', adjustDropdownPosition, true);
      if (bubbleMenu) {
        bubbleMenu.addEventListener('scroll', adjustDropdownPosition);
      }
      adjustDropdownPosition();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', adjustDropdownPosition);
      window.removeEventListener('scroll', adjustDropdownPosition, true);
      if (bubbleMenu) {
        bubbleMenu.removeEventListener('scroll', adjustDropdownPosition);
      }
    };
  }, [isDropdownOpen, adjustDropdownPosition]);

  const onSubmitUrlForm = useCallback(
    (e: React.SyntheticEvent) => {
      if (editor) {
        e.preventDefault();
        if (urlValue === null) return;
        if (urlValue === '') {
          editor.chain().focus().extendMarkRange('link').unsetLink().run();
          return;
        }
        editor.chain().focus().extendMarkRange('link').setLink({ href: urlValue }).run();
        setUrlValue('');
        setIsUrlOpen(false);
      }
    },
    [editor, setIsUrlOpen, setUrlValue, urlValue]
  );

  const onClickRemoveUrl = useCallback(() => {
    if (editor) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      setUrlValue('');
      setIsUrlOpen(false);
    }
  }, [editor, setIsUrlOpen, setUrlValue]);

  const onClickRedirectToUrl = useCallback(() => {
    if (!isEmpty(urlValue)) {
      window.open(urlValue, '_blank');
      if (editor) {
        editor.commands.setTextSelection(editor.state.selection.from);
      }
      setIsUrlOpen(false);
    }
  }, [editor, setIsUrlOpen, urlValue]);

  const setLink = useCallback(() => {
    if (editor) {
      const previousUrl = editor.getAttributes('link').href;
      setUrlValue(previousUrl ? previousUrl : '');
      editor.chain().focus().extendMarkRange('link').run();
      setIsUrlOpen(true);
    }
  }, [editor, setIsUrlOpen, setUrlValue]);

  if (isUrlOpen) {
    return (
      <>
        <BubbleMenu
          editor={editor}
          tippyOptions={{
            popperOptions: { strategy: 'fixed' },
            duration: 50,
            onHide: () => {
              setIsUrlOpen(false);
              rootStore.appStore.setIsBubbleMenuOpen(false);
              setUrlValue('');
            },
            onShow: () => {
              rootStore.appStore.setIsBubbleMenuOpen(true);
            }
          }}
          shouldShow={({ editor }) => {
            const isAnyEmbed =
              editor.isActive('imageUploadEmbed') ||
              editor.isActive('figmaEmbed') ||
              editor.isActive('projectLinkEmbed') ||
              editor.isActive('taskLinkEmbed') ||
              editor.isActive('youTubeEmbed') ||
              editor.isActive('snapEmbed');
            const { from, to } = editor.state.selection;
            const hasSelection = from !== to;
            return hasSelection && !isAnyEmbed;
          }}>
          <div className='link-menu'>
            <form onSubmit={onSubmitUrlForm}>
              <input
                placeholder='Enter URL'
                value={urlValue}
                onChange={(e) => setUrlValue(e.target.value)}
                aria-label='Insert link URL'
              />
            </form>
            <div className='line-div' />
            <IconButton
              iconName='expand-chat-icon'
              size={ICON_BUTTON_SIZE.MEDIUM}
              variant={ICON_BUTTON_TYPE.TERTIARY}
              onClick={onClickRedirectToUrl}
            />
            <IconButton
              iconName='updated-delete-icon'
              size={ICON_BUTTON_SIZE.MEDIUM}
              variant={ICON_BUTTON_TYPE.TERTIARY}
              onClick={onClickRemoveUrl}
            />
          </div>
        </BubbleMenu>
      </>
    );
  }

  return (
    <>
      <BubbleMenu
        editor={editor}
        tippyOptions={{
          popperOptions: { strategy: 'fixed' },
          duration: 50,
          interactive: true,
          maxWidth: isBubbleMenuMaxWidth ? 'unset' : '',
          onHide: () => {
            if (!isImproveModalOpen) {
              rootStore.appStore.setIsBubbleMenuOpen(false);
            }
          },
          onShow: () => {
            rootStore.appStore.setIsBubbleMenuOpen(true);
          }
        }}
        shouldShow={({ editor }) => {
          const isImage = editor.isActive('image');
          const isAnyEmbed =
            editor.isActive('figmaEmbed') ||
            editor.isActive('projectLinkEmbed') ||
            editor.isActive('taskLinkEmbed') ||
            editor.isActive('youTubeEmbed') ||
            editor.isActive('imageUploadEmbed') ||
            editor.isActive('snapEmbed');
          const { from, to } = editor.state.selection;
          const hasSelection = from !== to;
          return (isImproveModalOpen || hasSelection) && !isImage && !isAnyEmbed;
        }}>
        {isImproveModalOpen ? (
          <span />
        ) : (
          <div className='bubble-menu'>
            <Chip
              leadingIconName='magic-icon'
              leadingIconViewBox='0 0 16 16'
              title='Improve'
              variant={CHIP_TYPE.FILL_WITHOUT_BORDER}
              size={CHIP_SIZE.SMALL}
              onClick={(e?: React.MouseEvent) => {
                e?.stopPropagation();
                setImproveModel();
              }}
              textColorType='secondary'
            />
            <LineDiv />
            <button ref={buttonRef} onClick={toggleDropdown} className='heading-dropdown'>
              <SVGIcon
                name='heading-icon'
                width={ICON_SIZE.MD}
                height={ICON_SIZE.MD}
                viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
              />
              <SVGIcon name='down-arrow-icon' width='10' height='10' viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`} />
            </button>
            <IconButton
              iconName='bold-icon'
              size={ICON_BUTTON_SIZE.MEDIUM}
              variant={ICON_BUTTON_TYPE.TERTIARY}
              onClick={() => editor.chain().focus().toggleBold().run()}
            />
            <IconButton
              iconName='italic-icon'
              size={ICON_BUTTON_SIZE.MEDIUM}
              variant={ICON_BUTTON_TYPE.TERTIARY}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            />
            <IconButton
              iconName='underline-icon'
              size={ICON_BUTTON_SIZE.MEDIUM}
              variant={ICON_BUTTON_TYPE.TERTIARY}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
            />
            <IconButton
              iconName='strike-icon'
              size={ICON_BUTTON_SIZE.MEDIUM}
              variant={ICON_BUTTON_TYPE.TERTIARY}
              onClick={() => editor.chain().focus().toggleStrike().run()}
            />
            <IconButton
              iconName='copy-link-icon'
              size={ICON_BUTTON_SIZE.MEDIUM}
              variant={ICON_BUTTON_TYPE.TERTIARY}
              onClick={setLink}
            />
            <IconButton
              iconName='blockquote-icon'
              size={ICON_BUTTON_SIZE.MEDIUM}
              variant={ICON_BUTTON_TYPE.TERTIARY}
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
            />
            <IconButton
              iconName='inline-code-icon'
              size={ICON_BUTTON_SIZE.MEDIUM}
              variant={ICON_BUTTON_TYPE.TERTIARY}
              onClick={() => editor.chain().focus().toggleCode().run()}
            />
            <IconButton
              iconName='code-block-icon'
              size={ICON_BUTTON_SIZE.MEDIUM}
              variant={ICON_BUTTON_TYPE.TERTIARY}
              onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            />
            <IconButton
              iconName='bullet-list-icon'
              size={ICON_BUTTON_SIZE.MEDIUM}
              variant={ICON_BUTTON_TYPE.TERTIARY}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            />
          </div>
        )}
      </BubbleMenu>
      {isDropdownOpen && (
        <TiptapDropdown
          dropdownRef={dropdownRef}
          dropdownCoords={dropdownCoords}
          editor={editor}
          setDropdownOpen={setDropdownOpen}
        />
      )}
    </>
  );
};

export default TiptapBubbleMenu;
